/* ==========================================================================
   VYORO B2B — Predictive search
   Backed entirely by Shopify's Predictive Search API. Results are whatever
   Shopify returns; nothing is synthesised client-side.

   Recent searches live in the visitor's own localStorage — Shopify has no
   native recent-search store — and never leave the browser.
   ========================================================================== */

(function () {
  'use strict';

  const VYORO = (window.VYORO = window.VYORO || {});
  const utils = VYORO.utils;
  const routes = VYORO.routes;

  const RECENT_KEY = 'vyoro:recent-searches';
  const RECENT_LIMIT = 6;
  const MIN_QUERY_LENGTH = 2;

  const overlay = document.getElementById('SearchOverlay');
  if (!overlay) return;

  const input = overlay.querySelector('[data-predictive-search-input]');
  const results = overlay.querySelector('[data-predictive-search-results]');
  const defaultState = overlay.querySelector('[data-search-default]');
  const loadingState = overlay.querySelector('[data-search-loading]');
  const output = overlay.querySelector('[data-search-output]');
  const form = overlay.querySelector('[data-predictive-search-form]');

  let abortController = null;

  /* ------------------------------------------------------------------------
     Recent searches
     ---------------------------------------------------------------------- */

  function readRecent() {
    try {
      const raw = window.localStorage.getItem(RECENT_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      // Private mode, disabled storage, or corrupt data — behave as if empty.
      return [];
    }
  }

  function writeRecent(list) {
    try {
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_LIMIT)));
    } catch (error) {
      // Storing recent searches is a convenience; failure is not an error.
    }
  }

  function rememberQuery(query) {
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) return;

    const list = readRecent().filter((item) => item.toLowerCase() !== trimmed.toLowerCase());
    list.unshift(trimmed);
    writeRecent(list);
  }

  function renderRecent() {
    const wrapper = overlay.querySelector('[data-recent-searches]');
    const list = overlay.querySelector('[data-recent-list]');
    if (!wrapper || !list) return;

    const recent = readRecent();
    if (!recent.length) {
      wrapper.hidden = true;
      return;
    }

    wrapper.hidden = false;
    list.innerHTML = '';

    recent.forEach((query) => {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'chip';
      chip.textContent = query;
      chip.addEventListener('click', () => {
        input.value = query;
        runSearch(query);
      });
      list.appendChild(chip);
    });
  }

  /* ------------------------------------------------------------------------
     Rendering
     ---------------------------------------------------------------------- */

  function showState(state) {
    if (defaultState) defaultState.hidden = state !== 'default';
    if (loadingState) loadingState.hidden = state !== 'loading';
    if (output) output.hidden = state !== 'results';
    if (input) input.setAttribute('aria-expanded', String(state === 'results'));
  }

  function escapeHTML(value) {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : value;
    return div.innerHTML;
  }

  /**
   * Classify how a result matched the query, mirroring the search-status
   * states in the design system. Only structural signals are used — an exact
   * SKU equality, or a match on the product type.
   */
  function classify(product, query) {
    const q = query.trim().toLowerCase();
    if (!q) return null;

    const sku = (product.sku || '').toLowerCase();
    if (sku && sku === q) return 'sku_match';
    if (sku && sku.includes(q)) return 'sku_match';

    const type = (product.type || '').toLowerCase();
    if (type && type.includes(q)) return 'category_match';

    return null;
  }

  function renderResults(data, query) {
    if (!output) return;

    const products = (data.resources && data.resources.results && data.resources.results.products) || [];
    const collections = (data.resources && data.resources.results && data.resources.results.collections) || [];
    const pages = (data.resources && data.resources.results && data.resources.results.pages) || [];

    if (!products.length && !collections.length && !pages.length) {
      output.innerHTML = `
        <div class="state-block">
          <div class="state-block__icon">
            <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75"
                 stroke-linecap="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/>
            </svg>
          </div>
          <h3>${escapeHTML(VYORO.strings.noResults || 'No results found')}</h3>
          <p>No products match &ldquo;${escapeHTML(query)}&rdquo;. Try a shorter query, a partial SKU, or browse by category.</p>
          <div style="margin-top: var(--space-5);">
            <a class="btn btn--secondary" href="${routes.search}?q=${encodeURIComponent(query)}">
              Search the full catalogue
            </a>
          </div>
        </div>
      `;
      showState('results');
      return;
    }

    let html = '';

    if (products.length) {
      html += '<div class="search-overlay__section-title"><h3>Products</h3></div>';
      html += products
        .map((product) => {
          const image = product.featured_image && product.featured_image.url;
          const status = classify(product, query);

          return `
            <a class="search-result" href="${product.url}">
              <div class="search-result__media media media--square">
                ${
                  image
                    ? `<img src="${image}" alt="${escapeHTML(product.title)}" loading="lazy" width="56" height="56">`
                    : ''
                }
              </div>
              <div class="search-result__info">
                <p class="search-result__title">${escapeHTML(product.title)}</p>
                <span class="search-result__meta">
                  ${product.sku ? escapeHTML(product.sku) : escapeHTML(product.type || '')}
                  ${status === 'sku_match' ? ' · SKU match' : ''}
                  ${status === 'category_match' ? ' · Category match' : ''}
                </span>
              </div>
              <span class="search-result__price tabular">${product.price ? utils.formatMoney(product.price) : ''}</span>
            </a>
          `;
        })
        .join('');
    }

    if (collections.length) {
      html += '<div class="search-overlay__section-title"><h3>Categories</h3></div>';
      html += '<div class="search-quick-links">';
      html += collections
        .map((collection) => `<a class="chip" href="${collection.url}">${escapeHTML(collection.title)}</a>`)
        .join('');
      html += '</div>';
    }

    if (pages.length) {
      html += '<div class="search-overlay__section-title"><h3>Pages</h3></div>';
      html += '<div class="search-quick-links">';
      html += pages
        .map((page) => `<a class="chip" href="${page.url}">${escapeHTML(page.title)}</a>`)
        .join('');
      html += '</div>';
    }

    html += `
      <div style="padding: var(--space-5) 0;">
        <a class="link-arrow" href="${routes.search}?q=${encodeURIComponent(query)}">
          View all results for &ldquo;${escapeHTML(query)}&rdquo;
        </a>
      </div>
    `;

    output.innerHTML = html;
    showState('results');
  }

  /* ------------------------------------------------------------------------
     Fetching
     ---------------------------------------------------------------------- */

  async function runSearch(query) {
    const trimmed = query.trim();

    if (trimmed.length < MIN_QUERY_LENGTH) {
      renderRecent();
      showState('default');
      return;
    }

    if (abortController) abortController.abort();
    abortController = new AbortController();

    showState('loading');

    const url = new URL(routes.predictiveSearch, window.location.origin);
    url.searchParams.set('q', trimmed);
    url.searchParams.set('resources[type]', 'product,collection,page');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,variants.sku,vendor');

    try {
      const response = await fetch(url.toString(), {
        signal: abortController.signal,
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) throw new Error(`Search failed: ${response.status}`);

      const data = await response.json();
      renderResults(data, trimmed);
    } catch (error) {
      if (error.name === 'AbortError') return;

      // Recoverable state: offer the full search page rather than an error.
      if (output) {
        output.innerHTML = `
          <div class="state-block">
            <h3>Search is unavailable right now</h3>
            <p>You can still search the full catalogue.</p>
            <div style="margin-top: var(--space-5);">
              <a class="btn btn--secondary" href="${routes.search}?q=${encodeURIComponent(trimmed)}">
                Search the full catalogue
              </a>
            </div>
          </div>
        `;
        showState('results');
      }
    }
  }

  /* ------------------------------------------------------------------------
     Wiring
     ---------------------------------------------------------------------- */

  if (input) {
    input.addEventListener(
      'input',
      utils.debounce((event) => runSearch(event.target.value), 250)
    );

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') rememberQuery(input.value);
    });
  }

  if (form) {
    form.addEventListener('submit', () => rememberQuery(input.value));
  }

  const clearRecent = overlay.querySelector('[data-clear-recent]');
  if (clearRecent) {
    clearRecent.addEventListener('click', () => {
      writeRecent([]);
      renderRecent();
    });
  }

  overlay.addEventListener('overlay:open', () => {
    renderRecent();
    if (!input.value.trim()) showState('default');
  });

  renderRecent();
})();
